import * as THREE from 'three';
import type { MaterialLibrary } from '../assets/MaterialLibrary';
import { Enemy } from '../entities/Enemy';
import { Projectile } from '../entities/Projectile';
import { Soldier } from '../entities/Soldier';
import { Tower } from '../entities/Tower';

export type CombatEvent =
  | { type: 'shot'; position: THREE.Vector3; towerType: 'mg' | 'cannon' | 'at' }
  | { type: 'soldier-shot'; position: THREE.Vector3 }
  | { type: 'hit'; position: THREE.Vector3; enemy: Enemy; damage: number; splash: boolean }
  | { type: 'objective-damage'; amount: number }
  | { type: 'enemy-death'; enemy: Enemy; position: THREE.Vector3 };

export class CombatSystem {
  private readonly events: CombatEvent[] = [];

  update(
    delta: number,
    towers: Tower[],
    soldiers: Soldier[],
    enemies: Enemy[],
    projectiles: Projectile[],
    _objectivePosition: THREE.Vector3,
    _materials: MaterialLibrary,
    onSpawnProjectile: (projectile: Projectile) => void,
  ): CombatEvent[] {
    this.events.length = 0;

    for (const tower of towers) {
      const target = this.findTowerTarget(tower, enemies);
      tower.update(delta, target?.group.position ?? null);

      if (target && tower.canFire()) {
        tower.fire();
        const start = new THREE.Vector3();
        tower.muzzleSocket.getWorldPosition(start);
        const projectile = new Projectile(
          start,
          target.group.position,
          tower.stats.projectileSpeed,
          _materials,
          (position) =>
            this.onProjectileHit(
              position,
              { damage: tower.stats.damage, splashRadius: tower.stats.splashRadius, armorPiercing: tower.stats.armorPiercing },
              target,
              enemies,
              _materials,
            ),
          tower.stats.splashRadius > 0,
        );
        onSpawnProjectile(projectile);
        this.events.push({ type: 'shot', position: start.clone(), towerType: tower.stats.type });
      }
    }

    for (const soldier of soldiers) {
      const target = this.findSoldierTarget(soldier, enemies);
      const attackEvent = soldier.update(delta, target?.group.position ?? null);

      if (attackEvent) {
        const projectile = new Projectile(
          attackEvent.position,
          attackEvent.targetPosition,
          soldier.stats.projectileSpeed,
          _materials,
          (position) =>
            this.onProjectileHit(
              position,
              { damage: soldier.stats.damage, splashRadius: 0, armorPiercing: false },
              target!,
              enemies,
              _materials,
            ),
          false,
        );
        onSpawnProjectile(projectile);
        this.events.push({ type: 'soldier-shot', position: attackEvent.position.clone() });
      }
    }

    for (const projectile of projectiles) {
      if (!projectile.active) continue;
      projectile.update(delta);

      const nearest = this.findNearestEnemy(projectile.group.position, enemies);
      if (nearest && projectile.group.position.distanceTo(nearest.group.position) < nearest.radius + 0.15) {
        projectile.hit();
      } else if (this.isOutOfBounds(projectile.group.position)) {
        projectile.active = false;
      }
    }

    for (const enemy of enemies) {
      if (enemy.reachedObjective) {
        this.events.push({ type: 'objective-damage', amount: enemy.stats.damageToObjective });
      }
    }

    return this.events.slice();
  }

  private findTowerTarget(tower: Tower, enemies: Enemy[]): Enemy | null {
    let best: Enemy | null = null;
    let bestProgress = -1;

    for (const enemy of enemies) {
      if (!enemy.active) continue;
      const dist = tower.group.position.distanceTo(enemy.group.position);
      if (dist > tower.stats.range + enemy.radius) continue;
      if (enemy.pathProgress > bestProgress) {
        bestProgress = enemy.pathProgress;
        best = enemy;
      }
    }

    return best;
  }

  private findSoldierTarget(soldier: Soldier, enemies: Enemy[]): Enemy | null {
    let nearest: Enemy | null = null;
    let nearestDist = Infinity;

    for (const enemy of enemies) {
      if (!enemy.active) continue;
      const dist = soldier.group.position.distanceTo(enemy.group.position);
      if (dist > soldier.stats.range + enemy.radius) continue;
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = enemy;
      }
    }

    return nearest;
  }

  private findNearestEnemy(position: THREE.Vector3, enemies: Enemy[]): Enemy | null {
    let nearest: Enemy | null = null;
    let nearestDist = Infinity;

    for (const enemy of enemies) {
      if (!enemy.active) continue;
      const dist = position.distanceTo(enemy.group.position);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = enemy;
      }
    }

    return nearest;
  }

  private onProjectileHit(
    position: THREE.Vector3,
    source: { damage: number; splashRadius: number; armorPiercing: boolean },
    _primaryTarget: Enemy,
    enemies: Enemy[],
    _materials: MaterialLibrary,
  ): void {
    const splash = source.splashRadius > 0;
    const radius = source.splashRadius;

    for (const enemy of enemies) {
      if (!enemy.active && !enemy.reachedObjective) continue;
      const dist = position.distanceTo(enemy.group.position);
      const effectiveRadius = splash ? radius + enemy.radius : enemy.radius + 0.25;

      if (dist <= effectiveRadius) {
        let damage = source.damage;
        if (splash) {
          damage *= Math.max(0.35, 1 - dist / (radius + 0.001));
        }
        if (!source.armorPiercing && enemy.stats.type === 'tank') {
          damage *= 0.35;
        }

        enemy.takeDamage(damage);
        this.events.push({ type: 'hit', position: position.clone(), enemy, damage, splash });

        if (!enemy.active && !enemy.reachedObjective) {
          this.events.push({ type: 'enemy-death', enemy, position: enemy.group.position.clone() });
        }
      }
    }
  }

  private isOutOfBounds(position: THREE.Vector3): boolean {
    return position.length() > 30;
  }
}
